import {Injectable} from "@angular/core";

@Injectable({
  providedIn: 'root'
})
export class UtilService {

  /**
   * Removes currency formatting characters ($, commas) from a string.
   * @param value - The raw currency string (e.g., "$1,500.00")
   * @returns The cleaned string (e.g., "1500.00")
   */
  sanitizeCurrencyString(value: string): string {
    return value.replace(/[$,]/g, '');
  }

  /**
   * Parses a currency string into a number, handling formatted values.
   * @param value - The raw currency string (e.g., "$1,500.00")
   * @returns The numeric value, or null if invalid
   */
  parseCurrencyValue(value: string): number | null {
    const cleanedValue = this.sanitizeCurrencyString(value);
    if (cleanedValue === '' || cleanedValue === '.') {
      return null;
    }
    const numericValue = parseFloat(cleanedValue);
    return isNaN(numericValue) ? null : numericValue;
  }

  /**
   * Converts an amount in cents to dollars.
   * @param cents - The amount in cents
   * @returns The amount in dollars
   */
  centsToDollars(cents: number): number {
    return cents / 100;
  }

  makeReadable(key: string) {
    return key.replace(/([A-Z])/g, ' $1') // insert space before uppercase
      .replace(/^./, str => str.toUpperCase()) // capitalize first letter
      .trim();
  }

  /**
   * Escapes a single CSV field per RFC 4180: anything containing a comma, double quote or newline is
   * wrapped in double quotes, with inner quotes doubled.
   *
   * Every money value in these reports is rendered by FormatCurrencyPipe, which inserts thousands
   * separators — so any amount of $1,000.00 or more carries a comma and MUST be quoted. Unquoted, the
   * reader splits `$1,203.23` into `$1` and `203.23` and shifts every following column one to the
   * right, silently corrupting the rest of the row.
   */
  public csvField(valueP: unknown): string {
    const cell = valueP === null || valueP === undefined ? '' : String(valueP);
    return /[",\r\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
  }

  /**
   * Builds one CSV line from its fields, escaping each. Always prefer this over interpolating values
   * into a template literal — that is what let the thousands separator break the column alignment.
   */
  public csvRow(valuesP: unknown[]): string {
    return valuesP.map(valueP => this.csvField(valueP)).join(',');
  }

  public generateTableCSV(dataArrayP: any[], filenameP: string) {
    if (!dataArrayP || dataArrayP.length === 0) {
      console.warn('No data to export');
      return;
    }

    // Get headers from first object's keys
    const headers = Object.keys(dataArrayP[0]);
    const headerLine = this.csvRow(headers);

    // Build data rows
    const dataRows = dataArrayP.map(row => this.csvRow(headers.map(header => row[header] ?? '')));

    const csvContent = [headerLine, ...dataRows].join('\n');
    this.triggerDownload(csvContent, filenameP);
  }

  public downloadMultipleVerticalCSV(formattedDataP: any, filenameP: string) {
    const keys = Object.keys(formattedDataP[0]);

    // Header row: first col = "Field", then one col per report
    const headerLine = this.csvRow(['Field', ...formattedDataP.map((_: any, i: any) => `Report ${i + 1}`)]);

    // Build rows vertically
    const rows = keys.map(k => this.csvRow([
      this.makeReadable(k),
      ...formattedDataP.map((r: any) => r[k] ?? '')
    ]));

    const csvContent = [headerLine, ...rows].join('\n');

    this.triggerDownload(csvContent, filenameP)
  }

  triggerDownload(contentP: string, fileNameP: string) {
    const blob = new Blob([contentP], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileNameP;
    link.click();
    URL.revokeObjectURL(url);
  }
}
